import { useNavigate } from 'react-router-dom';
import { useHistory } from './useHistory';
import { useNavigateDone } from './useNavigateDone';
import { useGiftContext } from '../Context/GiftContextProvider';
import useGiftQuery from './useGiftQuery';
import useOrderQuery from './useOrderQuery';
import { useEffect, useState } from 'react';
import { GiftInformation } from '../../../api/gift';
import { WizardStep } from '../../../consts/wizardSteps';

const useGiftHomePage = () => {
  const { search, replace } = useHistory();
  const {
    setKlydoGiftProps,
    klydoGiftProps,
    setSelectedKlydoGreet,
    startNewGift,
  } = useGiftContext();

  const { data: orderInfo, isLoading: isLoadingOrderInfo } = useOrderQuery(
    (search.orderName || search.orderId) as string,
  );
  const { data: giftInfo, isLoading: isLoadingGift } = useGiftQuery({
    orderName:
      (search.basedOn as string) ||
      ((search.orderName || search.orderId) as string),
    clockId: search.clockId as string,
  });
  const gift = giftInfo?.gift;

  const [enterClockDialogOpen, setEnterClockDialogOpen] = useState(false);
  const [enterOrderNameDialogOpen, setEnterOrderNameDialogOpen] =
    useState(false);

  const navigate = useNavigate();
  const navigateToDone = useNavigateDone();

  const disabledCreateBasedOnGreet = search?.basedOn && !gift;

  useEffect(() => {
    if (search.basedOn && gift) {
      setKlydoGiftProps({
        klydoId: gift.klydoId,
        senderImageUrl: gift?.senderImageUrl,
        senderName: gift.senderName,
        date: gift?.date,
        greetingContent: gift.greetingContent,
        pendulumLogoUrl: gift?.pendulumLogoUrl,
        pendulumLogoType: gift?.pendulumLogoType,
      });
      setSelectedKlydoGreet(gift.klydo);
    }
  }, [search.basedOn, gift]);

  useEffect(() => {
    const orderName = search?.orderName || search?.orderId;
    const clockId = search?.clockId;
    let timeZone: string | undefined;
    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {}
    setKlydoGiftProps({
      ...klydoGiftProps,
      orderName: orderName as string,
      clockId: clockId as string,
      timeZone,
    });
  }, []);

  // A new greet keeps only where it goes. Everything else from a gift opened
  // earlier in this tab (logo, name, greeting, giftId) must not carry over.
  const startNewGiftFor = (target: { orderName?: string; clockId?: string }) =>
    startNewGift({
      orderName: klydoGiftProps?.orderName,
      clockId: klydoGiftProps?.clockId,
      timeZone: klydoGiftProps?.timeZone,
      ...target,
    });

  const handleCreateGreet = () => {
    if ((search.basedOn && gift) || search.byOrder) {
      setEnterOrderNameDialogOpen(true);
      return;
    }
    if (
      !klydoGiftProps?.orderName &&
      !klydoGiftProps?.clockId &&
      !search.basedOn
    ) {
      setEnterClockDialogOpen(true);
      return;
    }
    if (!search.basedOn) startNewGiftFor({});
    navigate(`/gift/wizard?step=${WizardStep.OCCASION}`);
  };

  const prsonalInfo = orderInfo?.name ?? '';

  if (isLoadingGift) return { isLoading: true };

  if (gift && !search.basedOn) {
    setKlydoGiftProps(gift);
    setSelectedKlydoGreet(gift.klydo);
    if (giftInfo && giftInfo.clockInfo?.id) {
      navigateToDone({ clockId: giftInfo.clockInfo.id });
    } else if (gift.orderName) {
      navigateToDone({ orderName: gift.orderName });
    }
  }

  if (isLoadingOrderInfo) return { isLoading: true };

  const handleClockConfirm = async (giftInformation: GiftInformation) => {
    if (giftInformation.gift) {
      setKlydoGiftProps(giftInformation.gift);
      setSelectedKlydoGreet(giftInformation.gift.klydo);
      navigateToDone({ clockId: giftInformation.clockInfo?.id });
      return;
    }
    startNewGiftFor({ clockId: giftInformation.clockInfo?.id });
    navigate(`/gift/wizard?step=${WizardStep.OCCASION}`);
    setEnterClockDialogOpen(false);
  };

  const handleAddOrderName = async (
    giftInformation: GiftInformation,
    orderName: string,
  ) => {
    if (giftInformation?.gift) {
      setKlydoGiftProps(giftInformation.gift);
      setSelectedKlydoGreet(giftInformation.gift.klydo);
      navigateToDone({ orderName: giftInformation.gift.orderName });
      return;
    }
    // "Based on" copied the source greet into state on purpose: keep it.
    if (search.basedOn) setKlydoGiftProps({ ...klydoGiftProps, orderName });
    else startNewGiftFor({ orderName });
    replace(
      `/gift/wizard?step=${search.basedOn ? WizardStep.PERSONAL_GREET : WizardStep.OCCASION}`,
    );
    setEnterOrderNameDialogOpen(false);
  };

  return {
    isLoading: false,
    handleCreateGreet,
    prsonalInfo,
    navigate,
    enterClockDialogOpen,
    handleCloseEnterClockDialog: () => setEnterClockDialogOpen(false),
    handleClockConfirm,
    enterOrderNameDialogOpen,
    handleAddOrderName,
    handleCloseEnterOrderNameDialog: () => setEnterOrderNameDialogOpen(false),
    disabledCreateBasedOnGreet,
  };
};

export default useGiftHomePage;
